/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Button, Typography } from "@mui/material";
import ContentShell from "components/ContentShell";
import configData from "config/config.json";
import { useSnackbar } from "hooks/useSnackbar";
import { SimpleTextInput } from "components/FormTextInput";
import {
  createEOSRewardConfig,
  getEOSRewardConfigInfo,
  updateEOSRewardConfig,
} from "services/eosRewardConfigServices";
import { getPlayerPacks, packInfo } from "services/packServices";
import AddIcon from "@mui/icons-material/Add";
import { eosRewardConfigValidation } from "utils/validation";
import SearchableInput from "components/SearchableInput";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import { makeStyles } from "@mui/styles";
import { defaultPageNo, defaultPageSize } from "utils/constants";

const useStyles = makeStyles((theme) => ({
  container: {
    padding: "20px",
    border: "1px solid #ddd",
    borderRadius: "8px",
    backgroundColor: "#f9f9f9",
    [theme.breakpoints.down("sm")]: {
      display: "flex",
      flexDirection: "column",
    },
  },
  field: {
    width: 220,
  },
  buttonContainer: {
    display: "flex",
    gap: 10,
  },
  firstFormContainer: {
    display: "flex",
    flexFlow: "row wrap",
    gap: 4,
    margin: "0px 0px 10px 0px",
  },
  secondFormContainer: {
    display: "flex",
    flexFlow: "row wrap",
    gap: 25,
    margin: "10px 0px 20px 0px",
  },
  rewardPackContainer: {
    display: "flex",
    alignItems: "end",
    gap: "10px",
    width: "100%",
  },
}));

const DEFAULT_CONFIG_DETAILS = {
  tier: 0,
  startPoints: 0,
  endPoints: 0,
  rewards: [
    {
      rewardPack: "",
      rewardPackCount: 0,
    },
  ],
};

const EOSRewardConfigEditor = () => {
  const styles = useStyles();
  const location = useLocation();
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const id = location.pathname.split("/").slice(-1)[0];
  const isCreateMode = id === "create";

  const [loading, setLoading] = useState(false);
  const [configDetails, setConfigDetails] = useState(DEFAULT_CONFIG_DETAILS);
  const [rewardDetails, setRewardDetails] = React.useState({
    rewardOptions: [],
    rewards: [],
  });

  const [searchPagination] = useState({
    pageNo: defaultPageNo,
    pageSize: defaultPageSize,
    hasMore: false,
  });

  const transformRewardPacks = (packs = []) => {
    return packs.map((pack) => ({
      value: pack._id, // Some components expect 'value' instead of 'id'
      id: pack._id, // Keep id for backward compatibility
      label: `${pack.name || "Unnamed Pack"} - ${pack.type || "No Type"} - ${
        pack._id
      }`,
    }));
  };

  const onSearch = (searchValue = "") => {
    if (searchValue !== "")
      fetchRewardPacks({
        search: searchValue,
        skipPagination: true,
        ...searchPagination,
      });
  };

  const fetchRewardPacks = async (filters = {}, init = false) => {
    setRewardDetails((st) => ({ ...st, isFetched: false }));
    try {
      const response = await getPlayerPacks(filters);
      const options = transformRewardPacks(response.items);
      setRewardDetails((st) => {
        let data = { ...st };
        if (init) data.rewards = response.items;
        return {
          ...data,
          rewardOptions: options,
        };
      });

      return options;
    } catch (error) {
      showSnackbar(error.message, "error");
    }
  };

  const cancel = () => navigate(-1);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      console.log({ configDetails });

      await eosRewardConfigValidation.validate(configDetails, {
        abortEarly: false,
      });
      const payload = {
        tier: Number(configDetails.tier),
        startPoints: Number(configDetails.startPoints),
        endPoints: Number(configDetails.endPoints),
        rewards: configDetails.rewards.map((reward) => ({
          rewardPack: reward.rewardPack,
          rewardPackCount: Number(reward.rewardPackCount),
        })),
      };
      const hasValidRewards = payload.rewards.some(
        (reward) => reward.rewardPack
      );
      if (!hasValidRewards) {
        throw new Error("At least one reward must have a pack selected");
      }

      const response = isCreateMode
        ? await createEOSRewardConfig(payload)
        : await updateEOSRewardConfig(payload, id);

      showSnackbar(response.message, "success");
      navigate(configData.routes.eosRewardConfig.eosRewardConfig);
    } catch (error) {
      showSnackbar(error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setConfigDetails((prev) => ({ ...prev, [name]: value }));
  };

  const handleRewardChange = (index, e) => {
    const { name, value } = e.target || {};

    setConfigDetails((prev) => {
      const updatedRewards = [...prev.rewards];

      if (name === "rewardPack") {
        const packId = value?.id || value?.value;
        updatedRewards[index] = {
          ...updatedRewards[index],
          rewardPack: packId,
        };
      } else {
        updatedRewards[index] = {
          ...updatedRewards[index],
          [name]: value,
        };
      }

      return {
        ...prev,
        rewards: updatedRewards,
      };
    });
  };

  const addReward = () => {
    setConfigDetails((prev) => ({
      ...prev,
      rewards: [
        ...prev.rewards,
        {
          rewardPack: "",
          rewardPackCount: 0,
        },
      ],
    }));
  };

  const removeReward = (index: any) => {
    setConfigDetails((prev) => {
      return {
        ...prev,
        rewards: [
          ...prev.rewards.slice(0, index),
          ...prev.rewards.slice(index + 1),
        ],
      };
    });
  };

  const fetchConfigInfo = async (rewardOP = []) => {
    try {
      setLoading(true);
      const resp = await getEOSRewardConfigInfo(id);
      setConfigDetails(resp.data);

      const requests = [];

      for (let reward of resp?.data?.rewards || []) {
        const data = rewardOP.find((el) => el.id === reward.rewardPack);
        if (!data) requests.push(packInfo(reward.rewardPack));
      }

      if (!requests.length) return;

      const results = await Promise.allSettled(requests);
      const newRewards = [];

      results.forEach((result) => {
        if (result.status === "fulfilled" && result.value.status) {
          newRewards.push(result.value.data);
        } else if (result.status === "rejected") {
          showSnackbar(
            result.reason?.message ?? "Could not find the reward pack",
            "error"
          );
        }
      });

      setRewardDetails((st) => ({
        ...st,
        rewardOptions: [
          ...st.rewardOptions,
          ...transformRewardPacks(newRewards),
        ],
        rewards: [...st.rewards, ...newRewards],
      }));
    } catch (error) {
      showSnackbar(error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    const getData = async () => {
      const data = await fetchRewardPacks(searchPagination, true);
      id !== "create" && (await fetchConfigInfo(data));
    };
    getData();
  }, []);

  return (
    <ContentShell
      showBackButton
      title={`${isCreateMode ? "Create" : "Edit"} EOS Reward Config`}
      loading={loading}
      actions={
        <Box className={styles.buttonContainer}>
          <Button
            variant="contained"
            style={{ backgroundColor: "red", color: "white" }}
            onClick={cancel}
          >
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSubmit}>
            Save
          </Button>
        </Box>
      }
    >
      <Box className={styles.container}>
        <Box className={styles.firstFormContainer}>
          <Box className={styles.field}>
            <SimpleTextInput
              label="Tier"
              name="tier"
              value={configDetails.tier}
              onChange={handleChange}
              type="number"
            />
          </Box>
          <Box className={styles.field}>
            <SimpleTextInput
              label="Start Points"
              name="startPoints"
              value={configDetails.startPoints}
              onChange={handleChange}
              type="number"
            />
          </Box>
          <Box className={styles.field}>
            <SimpleTextInput
              label="End Points"
              name="endPoints"
              value={configDetails.endPoints}
              onChange={handleChange}
              type="number"
            />
          </Box>
        </Box>

        <Box className={styles.secondFormContainer}>
          <Typography variant="h6" style={{ width: "100%" }}>
            Rewards
          </Typography>
          {configDetails.rewards.map((reward, index) => {
            const _rewardPack = rewardDetails.rewardOptions.find(
              (_) => _.id === reward.rewardPack
            );
            const selected = _rewardPack || {
              label: reward.rewardPack,
              id: reward.rewardPack,
              value: reward.rewardPack,
            };
            return (
              <Box key={index} className={styles.rewardPackContainer}>
                <Box className={styles.field}>
                  <SearchableInput
                    label="Reward Pack"
                    name="rewardPack"
                    selected={selected}
                    options={rewardDetails.rewardOptions}
                    onInputChange={onSearch}
                    onOptionSelect={(e) => handleRewardChange(index, e)}
                    getOptionLabel={(option) => option.label}
                    isOptionEqualToValue={(option, value) =>
                      option.id === value?.id
                    }
                  />
                </Box>
                <Box className={styles.field}>
                  <SimpleTextInput
                    label="Count"
                    name="rewardPackCount"
                    value={reward.rewardPackCount}
                    onChange={(e) => handleRewardChange(index, e)}
                    type="number"
                  />
                </Box>
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => removeReward(index)}
                >
                  <DeleteForeverIcon sx={{ color: 'white' }} />
                  <span style={{ marginLeft: 5 }}>{"delete"}</span>
                </Button>
                <Button size="small" variant="contained" onClick={addReward}>
                  <AddIcon sx={{ color: 'white' }} />
                  <span style={{ marginLeft: 5 }}>{"add"}</span>
                </Button>
              </Box>
            );
          })}
        </Box>
      </Box>
    </ContentShell>
  );
};

export default EOSRewardConfigEditor;
